/**
 * User preferences page component.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { updateCurrentUserProfile } from '../api/users';
import { Layout } from '../components/Layout';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';
import './PreferencesPage.css';

export const PreferencesPage: React.FC = () => {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { isConnected, on, off } = useWebSocket();

  // Update form fields when user data changes
  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setName(user.name);
    }
  }, [user]);

  // Register WebSocket event handlers for user updates
  useEffect(() => {
    const handleUserUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, user_id, user_data } = message.data || {};
      
      // Only handle updates for the current user
      if (user && user_id === user.id && event_type === 'updated') {
        // Update form fields from WebSocket message if available
        if (user_data) {
          if (user_data.email !== undefined) {
            setEmail(user_data.email);
          }
          if (user_data.name !== undefined) {
            setName(user_data.name);
          }
        }
        // Refresh user data from context
        await refreshUser();
      }
    };

    const unsubscribe = on('user_update', handleUserUpdate);

    return () => {
      unsubscribe();
    };
  }, [user, on, off, refreshUser]);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'auto') => {
    setTheme(newTheme);
  };

  const handleProfileSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setIsLoading(true);

    // Validate passwords match if password is provided
    if (password && password !== confirmPassword) {
      setProfileError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const updateData: { email?: string; name?: string; password?: string } = {};
      
      if (email !== user?.email) {
        updateData.email = email;
      }
      if (name !== user?.name) {
        updateData.name = name;
      }
      if (password) {
        updateData.password = password;
      }

      await updateCurrentUserProfile(updateData);
      setProfileSuccess('Profile updated successfully!');
      
      // Clear password fields
      setPassword('');
      setConfirmPassword('');
      
      // Refresh user data to show updated information
      await refreshUser();
    } catch (err: any) {
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setProfileError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="preferences-page">
        <div className="preferences-container">
          <h1>Preferences</h1>

          <section className="preferences-section">
            <h2>Profile</h2>
            <p className="preferences-description">
              Update your email address, name, and password.
            </p>

            <form onSubmit={handleProfileSubmit} className="profile-form">
              {profileError && (
                <div className="error-message" role="alert">
                  <strong>Error:</strong> {profileError}
                </div>
              )}

              {profileSuccess && (
                <div className="success-message" role="alert">
                  {profileSuccess}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="profile-email">Email</label>
                <input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (profileError) setProfileError('');
                  }}
                  required
                  disabled={isLoading}
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="profile-name">Full Name</label>
                <input
                  id="profile-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (profileError) setProfileError('');
                  }}
                  required
                  disabled={isLoading}
                  placeholder="Enter your full name"
                  minLength={1}
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label htmlFor="profile-password">New Password (leave blank to keep current)</label>
                <input
                  id="profile-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (profileError) setProfileError('');
                  }}
                  disabled={isLoading}
                  placeholder="Enter new password"
                  minLength={8}
                />
                {password && <PasswordStrengthIndicator password={password} />}
              </div>

              {password && (
                <div className="form-group">
                  <label htmlFor="profile-confirm-password">Confirm New Password</label>
                  <input
                    id="profile-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (profileError) setProfileError('');
                    }}
                    disabled={isLoading}
                    placeholder="Confirm new password"
                    minLength={8}
                  />
                </div>
              )}

              <button type="submit" disabled={isLoading} className="save-button">
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </section>

          <section className="preferences-section">
            <h2>Theme</h2>
            <p className="preferences-description">
              Choose your preferred color theme. "Auto" will follow your system preference.
            </p>

            <div className="theme-options">
              <button
                className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                onClick={() => handleThemeChange('light')}
                aria-pressed={theme === 'light'}
              >
                <span className="theme-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4"/>
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
                  </svg>
                </span>
                <span className="theme-label">Light</span>
                {theme === 'light' && <span className="theme-check">✓</span>}
              </button>

              <button
                className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => handleThemeChange('dark')}
                aria-pressed={theme === 'dark'}
              >
                <span className="theme-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                  </svg>
                </span>
                <span className="theme-label">Dark</span>
                {theme === 'dark' && <span className="theme-check">✓</span>}
              </button>

              <button
                className={`theme-option ${theme === 'auto' ? 'active' : ''}`}
                onClick={() => handleThemeChange('auto')}
                aria-pressed={theme === 'auto'}
              >
                <span className="theme-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </span>
                <span className="theme-label">Auto</span>
                {theme === 'auto' && <span className="theme-check">✓</span>}
                {theme === 'auto' && (
                  <span className="theme-subtitle">
                    ({effectiveTheme === 'dark' ? 'Dark' : 'Light'})
                  </span>
                )}
              </button>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

